import React, { useState } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  Eye,
  EyeOff,
  Star,
  ImagePlus,
  Link,
  Upload
} from 'lucide-react';
import { useCMS } from '../../store/cmsStore';
import type { Hotel } from '../../store/cmsStore';

export default function HotelEditor() {
  const { data, addHotel, updateHotel, deleteHotel } = useCMS();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState<Omit<Hotel, 'id'>>({
    stars: 3,
    label: '',
    badge: '',
    images: [],
    active: true,
    order: 0,
  });
  const [newImageUrl, setNewImageUrl] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const handleEdit = (hotel: Hotel) => {
    setEditingId(hotel.id);
    setFormData({
      stars: hotel.stars,
      label: hotel.label,
      badge: hotel.badge,
      images: [...hotel.images],
      active: hotel.active,
      order: hotel.order,
    });
    setIsAdding(false);
  };

  const handleAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setFormData({
      stars: 3,
      label: '',
      badge: '',
      images: [],
      active: true,
      order: data.hotels.length + 1,
    });
  };

  const handleSave = () => {
    if (!formData.label.trim()) {
      alert('يرجى إدخال اسم الفندق');
      return;
    }
    if (editingId) {
      updateHotel(editingId, formData);
    } else {
      addHotel(formData);
    }
    setEditingId(null);
    setIsAdding(false);
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
  };

  const handleDelete = (id: string) => {
    if (confirm('هل أنت متأكد من حذف هذا الفندق؟')) {
      deleteHotel(id);
    }
  };

  const addImage = () => {
    if (newImageUrl.trim()) {
      setFormData({ ...formData, images: [...formData.images, newImageUrl.trim()] });
      setNewImageUrl('');
    }
  };

  const removeImage = (index: number) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index),
    });
  };

  const handleImageFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('يرجى اختيار ملف صورة فقط');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
      return;
    }
    setIsUploadingImage(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      setFormData({ ...formData, images: [...formData.images, base64] });
      setIsUploadingImage(false);
    };
    reader.onerror = () => {
      alert('خطأ في قراءة الملف');
      setIsUploadingImage(false);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const toggleActive = (id: string, currentActive: boolean) => {
    updateHotel(id, { active: !currentActive });
  };

  const sortedHotels = [...data.hotels].sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 font-amiri">إدارة الفنادق</h2>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 bg-gold-500 text-white rounded-lg hover:bg-gold-600 transition font-cairo"
        >
          <Plus className="w-4 h-4" />
          إضافة فندق جديد
        </button>
      </div>

      {/* Add/Edit Form */}
      {(isAdding || editingId) && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900 font-cairo">
              {editingId ? 'تعديل الفندق' : 'إضافة فندق جديد'}
            </h3>
            <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Label */}
            <div>
              <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                اسم الفندق *
              </label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-gold-500 focus:border-transparent font-cairo"
                placeholder="مثال: فنادق 3 نجوم"
              />
            </div>

            {/* Stars */}
            <div>
              <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                عدد النجوم
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={formData.stars}
                  onChange={(e) => setFormData({ ...formData, stars: parseInt(e.target.value) })}
                  className="flex-1"
                />
                <div className="flex gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < formData.stars ? 'text-yellow-400 fill-current' : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Badge */}
            <div>
              <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                الشارة
              </label>
              <input
                type="text"
                value={formData.badge}
                onChange={(e) => setFormData({ ...formData, badge: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-gold-500 focus:border-transparent font-cairo"
                placeholder="مثال: الباقة الاقتصادية"
              />
            </div>

            {/* Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                الترتيب
              </label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-gold-500 focus:border-transparent font-cairo"
                min="1"
              />
            </div>

            {/* Images */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                الصور
              </label>
              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <input
                  type="url"
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addImage()}
                  className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-gold-500 focus:border-transparent font-cairo"
                  placeholder="رابط الصورة"
                />
                <button
                  onClick={addImage}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-cairo"
                >
                  <ImagePlus className="w-4 h-4" />
                  إضافة
                </button>
                <label className="flex items-center justify-center gap-2 px-4 py-2 bg-gold-500 text-white rounded-lg hover:bg-gold-600 cursor-pointer font-cairo">
                  <Upload className="w-4 h-4" />
                  {isUploadingImage ? 'جاري الرفع...' : 'رفع صورة'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileUpload}
                    className="hidden"
                    disabled={isUploadingImage}
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {formData.images.map((url, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={url}
                      alt={`صورة ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg border border-gray-200"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23ddd" width="100" height="100"/><text fill="%23999" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="12">خطأ</text></svg>';
                      }}
                    />
                    <button
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              {formData.images.length === 0 && (
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                  <ImagePlus className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 font-cairo">لا توجد صور بعد</p>
                </div>
              )}
            </div>

            {/* Active Toggle */}
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4 text-gold-600 rounded focus:ring-gold-500"
                />
                <span className="text-sm font-cairo text-gray-700">نشط</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-100">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-cairo transition"
            >
              إلغاء
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-4 py-2 bg-gold-500 text-white rounded-lg hover:bg-gold-600 font-cairo transition"
            >
              <Save className="w-4 h-4" />
              حفظ
            </button>
          </div>
        </div>
      )}

      {/* Hotels List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 font-cairo">الفندق</th>
                <th className="text-center px-6 py-4 text-sm font-medium text-gray-500 font-cairo">النجوم</th>
                <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 font-cairo">الشارة</th>
                <th className="text-center px-6 py-4 text-sm font-medium text-gray-500 font-cairo">الصور</th>
                <th className="text-center px-6 py-4 text-sm font-medium text-gray-500 font-cairo">الحالة</th>
                <th className="text-center px-6 py-4 text-sm font-medium text-gray-500 font-cairo">الإجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedHotels.map((hotel) => (
                <tr key={hotel.id} className="hover:bg-gray-50 transition">
                  <td className="px-6 py-4">
                    <div className="font-bold text-gray-900 font-cairo">{hotel.label}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-0.5">
                      {Array.from({ length: hotel.stars }).map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-block bg-gold-100 text-gold-700 px-3 py-1 rounded-full text-xs font-cairo">
                      {hotel.badge}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-gray-600 font-cairo">{hotel.images.length} صورة</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => toggleActive(hotel.id, hotel.active)}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-cairo transition ${
                        hotel.active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {hotel.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {hotel.active ? 'نشط' : 'مخفي'}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleEdit(hotel)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                        title="تعديل"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(hotel.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="حذف"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.hotels.length === 0 && (
          <div className="text-center py-12">
            <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-cairo">لا توجد فنادق بعد</p>
          </div>
        )}
      </div>
    </div>
  );
}